/**
 * Protected Route Component
 * For Media Devoted | Created By Ciro Autiero
 */

import { ReactNode, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from '@/hooks';
import { LoadingSpinner } from '@/components/ui';

interface ProtectedRouteProps {
  children: ReactNode;
  /** Single permission required (user must have it) */
  requiredPermission?: string;
  /** Multiple permissions - user needs ANY one of them */
  requiredPermissions?: string[];
  requiredRole?: string | string[];
}

export function ProtectedRoute({
  children,
  requiredPermission,
  requiredPermissions,
  requiredRole,
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, hasPermission, refreshMe, user } = useAuth();
  const location = useLocation();
  const [rechecking, setRechecking] = useState(false);
  const [rechecked, setRechecked] = useState(false);

  const hasRequiredPerm =
    !requiredPermission &&
    (!requiredPermissions || requiredPermissions.length === 0)
      ? true
      : requiredPermission
        ? hasPermission(requiredPermission)
        : (requiredPermissions ?? []).some((p) => hasPermission(p));

  // If permissions were just changed (role edited), our in-memory user permissions can be stale.
  // Do a one-time /auth/me refresh before sending the user to Forbidden.
  useEffect(() => {
    setRechecked(false);
    setRechecking(false);
  }, [location.pathname, location.search, requiredPermission, requiredPermissions, requiredRole]);

  useEffect(() => {
    if (
      rechecked ||
      rechecking ||
      isLoading ||
      !isAuthenticated ||
      (hasRequiredPerm && (!requiredRole || hasRequiredRole(user?.role, requiredRole)))
    ) {
      return;
    }

    setRechecking(true);
    refreshMe()
      .catch(() => undefined)
      .finally(() => {
        setRechecking(false);
        setRechecked(true);
      });
  }, [
    hasPermission,
    hasRequiredPerm,
    isAuthenticated,
    isLoading,
    refreshMe,
    requiredPermission,
    requiredPermissions,
    requiredRole,
    rechecked,
    rechecking,
    user?.role,
  ]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (rechecking) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (requiredRole && !hasRequiredRole(user?.role, requiredRole)) {
    const from = `${location.pathname}${location.search}${location.hash}`;
    return (
      <Navigate
        to={`/forbidden?from=${encodeURIComponent(from)}&role=${encodeURIComponent(
          Array.isArray(requiredRole) ? requiredRole.join('|') : requiredRole,
        )}`}
        state={{
          from,
          requiredRole,
        }}
        replace
      />
    );
  }

  if (!hasRequiredPerm) {
    const from = `${location.pathname}${location.search}${location.hash}`;
    const perm = requiredPermission ?? requiredPermissions?.[0] ?? '';
    return (
      <Navigate
        to={`/forbidden?from=${encodeURIComponent(from)}&perm=${encodeURIComponent(perm)}`}
        state={{
          from,
          requiredPermission: perm,
        }}
        replace
      />
    );
  }

  return <>{children}</>;
}

function hasRequiredRole(userRole: string | undefined, required: string | string[]): boolean {
  const role = (userRole || '').toLowerCase();
  if (!role) return false;

  if (Array.isArray(required)) {
    return required.map((r) => r.toLowerCase()).includes(role);
  }

  return role === required.toLowerCase();
}
